type WebKitFullscreenElement = Element & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

type WebKitFullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
  webkitFullscreenEnabled?: boolean;
};

type FullscreenNavigatorLike = Pick<Navigator, 'userAgent' | 'platform' | 'maxTouchPoints'>;

const FULLSCREEN_OPTIONS: FullscreenOptions = { navigationUI: 'hide' };
const FULLSCREEN_CHANGE_EVENTS = ['fullscreenchange', 'webkitfullscreenchange'] as const;

export function shouldUseNativeFullscreen(navigatorLike: FullscreenNavigatorLike = navigator) {
  const platform = navigatorLike.platform || '';
  const userAgent = navigatorLike.userAgent || '';
  const maxTouchPoints = navigatorLike.maxTouchPoints || 0;

  const isIOSDevice = /iPad|iPhone|iPod/.test(platform) || /iPad|iPhone|iPod/.test(userAgent);
  const isIPadOSDesktopMode = platform === 'MacIntel' && maxTouchPoints > 1;

  return !isIOSDevice && !isIPadOSDesktopMode;
}

/**
 * 检测当前设备是否支持原生元素全屏（含 WebKit 前缀）。
 * 步骤 1：排除 iOS / iPadOS 桌面模式等无原生全屏能力的设备；
 * 步骤 2：检查 fullscreenEnabled 开关。
 */
export function isNativeFullscreenSupported(doc: WebKitFullscreenDocument = document) {
  if (!shouldUseNativeFullscreen()) return false;
  return Boolean(doc.fullscreenEnabled || doc.webkitFullscreenEnabled);
}

export function getFullscreenElement(doc: WebKitFullscreenDocument = document) {
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

export function isNativeFullscreenElement(
  element: Element,
  doc: WebKitFullscreenDocument = document
) {
  return getFullscreenElement(doc) === element;
}

export async function requestNativeFullscreen(element: WebKitFullscreenElement) {
  if (typeof element.requestFullscreen === 'function') {
    await element.requestFullscreen(FULLSCREEN_OPTIONS);
    return true;
  }

  if (typeof element.webkitRequestFullscreen === 'function') {
    await element.webkitRequestFullscreen();
    return true;
  }

  return false;
}

export async function exitNativeFullscreen(doc: WebKitFullscreenDocument = document) {
  if (!getFullscreenElement(doc)) return false;

  if (typeof doc.exitFullscreen === 'function') {
    await doc.exitFullscreen();
    return true;
  }

  if (typeof doc.webkitExitFullscreen === 'function') {
    await doc.webkitExitFullscreen();
    return true;
  }

  return false;
}

export function addFullscreenChangeListeners(
  doc: Document,
  listener: EventListenerOrEventListenerObject
) {
  for (const eventName of FULLSCREEN_CHANGE_EVENTS) {
    doc.addEventListener(eventName, listener);
  }

  return () => {
    for (const eventName of FULLSCREEN_CHANGE_EVENTS) {
      doc.removeEventListener(eventName, listener);
    }
  };
}
